import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { BRAND_NAME, SUPPORT_EMAIL } from "@/config/appConfig";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SP</span>
            </div>
            <span className="text-xl font-bold text-gray-900">{BRAND_NAME}</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-gray-500 mb-8">Last updated: December 2024</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 mb-4">
              By accessing or using SeatPilot ("Service"), operated by TicketKart, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-600 mb-4">
              SeatPilot provides a seating chart creation and management platform for event ticketing companies. Our Service includes:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Interactive seating layout builder</li>
              <li>Embeddable seat selection widget</li>
              <li>API access for integration with ticketing platforms</li>
              <li>Seat hold and reservation management</li>
              <li>Usage analytics and reporting</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 mb-4">
              To use our Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information</li>
              <li>Keep your password confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Subscription Plans and Billing</h2>
            <p className="text-gray-600 mb-4">
              We offer various subscription plans:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Free Trial:</strong> 1 month free, 1 event, 1 layout, up to 500 seats</li>
              <li><strong>Pay As You Go:</strong> £0.50 per successfully booked seat</li>
              <li><strong>Monthly Plan:</strong> £50/month for up to 10,000 seats, then £0.30/seat</li>
              <li><strong>Enterprise:</strong> Custom pricing based on requirements</li>
            </ul>
            <p className="text-gray-600 mt-4">
              <strong>Billing:</strong> Subscription fees are charged in advance. Pay-as-you-go fees are charged based on actual usage. All fees are non-refundable except as required by law.
            </p>
            <p className="text-gray-600 mt-4">
              <strong>Refunds:</strong> For pay-as-you-go plans, fees charged for successfully booked seats are non-refundable, even if the end customer receives a refund. If a refunded seat is re-booked, standard fees apply.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Cancellation</h2>
            <p className="text-gray-600 mb-4">
              You may cancel your subscription at any time. Upon cancellation:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Your subscription will remain active until the end of the current billing period</li>
              <li>No refunds will be provided for the remaining period</li>
              <li>Your data will be retained for 30 days, after which it may be deleted</li>
              <li>API access will be revoked immediately after the subscription period ends</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Acceptable Use</h2>
            <p className="text-gray-600 mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Reverse engineer or decompile any part of the Service</li>
              <li>Resell or redistribute the Service without authorization</li>
              <li>Use automated systems to access the Service in a manner that exceeds reasonable use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. API Usage</h2>
            <p className="text-gray-600 mb-4">
              API access is subject to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Rate limits as specified in our API documentation</li>
              <li>Proper authentication using provided API keys</li>
              <li>Compliance with all applicable laws and regulations</li>
              <li>Not sharing API keys with unauthorized parties</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-600 mb-4">
              The Service and its original content, features, and functionality are owned by TicketKart and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-600">
              You retain ownership of any content you create using our Service, including seating layouts and configurations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              To the maximum extent permitted by law, TicketKart shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.
            </p>
            <p className="text-gray-600">
              Our total liability shall not exceed the amount paid by you to us in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Service Availability</h2>
            <p className="text-gray-600 mb-4">
              We strive to maintain high availability but do not guarantee uninterrupted service. We may:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Perform scheduled maintenance with advance notice</li>
              <li>Suspend service for emergency maintenance</li>
              <li>Modify or discontinue features with reasonable notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-600 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-600 mb-4">
              We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through the Service. Continued use after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
            <p className="text-gray-600">
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-600 mt-2">
              <strong>Email:</strong> {SUPPORT_EMAIL}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          <p>&copy; {new Date().getFullYear()} {BRAND_NAME}. Powered by TicketKart. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/" className="hover:text-white">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
